import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Home as HomeIcon, ArrowLeft, Pencil, Plus, Trash2, Image as ImageIcon, Users, Youtube, Newspaper, MoveUp, MoveDown, FileText, UploadCloud, Sparkles } from "lucide-react";
import { committeeApi, galleryApi, videosApi, postsApi, settingsApi, siteImagesApi, readError } from "@/lib/api";
import { refreshSiteImages } from "@/hooks/useSiteImages";
import AdminExtras from "@/pages/AdminExtras";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const emptyCommittee = { id: null, name: "", role: "", bio: "", img: "", order: 0, group: "leadership" };
const emptyGallery = { id: null, title: "", category: "", caption: "", src: "", order: 0 };
const emptyVideo = { id: null, video_id: "", tag: "", title: "", duration: "", order: 0 };
const emptyPost = { id: null, tag: "", date: "", title: "", excerpt: "", img: "", order: 0 };

// -------------------- Site Images (settings.images.*) --------------------
const IMAGE_SLOTS = [
  { key: "heroLeft", slot: "hero_left", label: "Home hero — left mosaic photo",
    hint: "Vertical photo on the left of the homepage hero (e.g. a heritage or tea-garden shot).", aspect: "aspect-[4/5]" },
  { key: "heroRight", slot: "hero_right", label: "Home hero — right mosaic photo",
    hint: "Square photo on the right of the homepage hero (e.g. a clinical / physician moment).", aspect: "aspect-square" },
  { key: "whatImage", slot: "what_image", label: "Home — 'What to expect' image",
    hint: "Wide banner shown inside the What-to-expect card (auditorium, workshop hall, etc.).", aspect: "aspect-[16/9]" },
  { key: "loginAside", slot: "login_aside", label: "Login page — aside photo",
    hint: "Tall photo shown next to the sign-in form.", aspect: "aspect-[4/5]" },
  { key: "registerAside", slot: "register_aside", label: "Register page — aside photo",
    hint: "Tall photo shown next to the delegate registration form.", aspect: "aspect-[4/5]" },
  { key: "logoApi", slot: "logo_api", label: "Header/Footer logo — API India",
    hint: "Square logo for Association of Physicians of India.", aspect: "aspect-square" },
  { key: "logoAssam", slot: "logo_assam", label: "Header/Footer logo — Assam Chapter",
    hint: "Square logo for the Assam State Chapter.", aspect: "aspect-square" },
];

const resolveImageUrl = (raw) => {
  if (!raw) return "";
  if (raw.startsWith("/api/")) {
    return `${process.env.REACT_APP_BACKEND_URL || ""}${raw}`;
  }
  return raw;
};

const SiteImagesPanel = () => {
  const [images, setImages] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const s = await settingsApi.get();
      setImages((s && s.images) || {});
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, WebP, GIF or AVIF).");
      return;
    }
    setBusy((b) => ({ ...b, [slot]: true }));
    try {
      const res = await siteImagesApi.upload(slot, file);
      await load();
      await refreshSiteImages();
      toast.success(`Uploaded new image for “${slot.replace(/_/g, " ")}”.`);
      return res;
    } catch (err) {
      toast.error(readError(err) || "Upload failed.");
    } finally {
      setBusy((b) => ({ ...b, [slot]: false }));
    }
  };

  const clear = async (slot) => {
    setBusy((b) => ({ ...b, [slot]: true }));
    try {
      await siteImagesApi.clear(slot);
      await load();
      await refreshSiteImages();
      toast.success("Reset to default.");
    } catch (err) {
      toast.error(readError(err) || "Reset failed.");
    } finally {
      setBusy((b) => ({ ...b, [slot]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-site-images">
      <div className="rounded-2xl border border-apicon-navy/10 bg-apicon-mist/30 p-5 text-sm text-apicon-navy">
        <div className="font-semibold">Upload replaces the default image everywhere on the site.</div>
        <div className="mt-1 text-xs text-apicon-navy/70">
          Recommended: JPG or PNG under 10 MB. Landscape shots work best for banners; square shots for logos and mosaic tiles.
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {IMAGE_SLOTS.map((s) => {
          const currentUrl = resolveImageUrl(images[s.key] || "");
          const isBusy = !!busy[s.slot];
          const hasCustom = !!images[s.key];
          return (
            <div
              key={s.slot}
              className="overflow-hidden rounded-3xl border border-gray-100 bg-white"
              data-testid={`admin-image-card-${s.slot}`}
            >
              <div className={`bg-apicon-mist/40 ${s.aspect}`}>
                {currentUrl ? (
                  <img
                    src={currentUrl}
                    alt={s.label}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-apicon-navy/40">
                    Using default image
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="font-display text-base font-semibold text-apicon-navy">
                  {s.label}
                </div>
                <p className="mt-1 text-xs text-gray-500">{s.hint}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <label
                    className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-apicon-navy px-4 py-2 text-xs font-semibold text-white hover:bg-[#082C5A] ${isBusy ? "opacity-70" : ""}`}
                    data-testid={`admin-image-upload-${s.slot}`}
                  >
                    <UploadCloud className="h-3.5 w-3.5" />
                    {isBusy ? "Uploading…" : hasCustom ? "Replace image" : "Upload image"}
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      disabled={isBusy}
                      onChange={(e) => upload(s.slot, e.target.files?.[0])}
                    />
                  </label>
                  {hasCustom && (
                    <button
                      type="button"
                      onClick={() => clear(s.slot)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-medium text-apicon-navy hover:border-apicon-navy hover:bg-apicon-mist disabled:opacity-70"
                      data-testid={`admin-image-reset-${s.slot}`}
                    >
                      <Trash2 className="h-3 w-3" /> Reset to default
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Extract an 11-char YouTube video ID from any common URL format
// (watch?v=X, youtu.be/X, shorts/X, embed/X, live/X) or return the input
// unchanged if it already looks like a raw ID.
const extractYoutubeId = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  // Already a bare 11-char ID
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  // Common URL patterns — grab the segment after v=, /shorts/, /embed/, /live/, or youtu.be/
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m) return m[1];
  }
  // Fall back: return the original so validation surfaces it to the user
  return s;
};

const ENTITY_API = {
  committee: committeeApi,
  gallery: galleryApi,
  videos: videosApi,
  posts: postsApi,
};
const EMPTY = { committee: emptyCommittee, gallery: emptyGallery, videos: emptyVideo, posts: emptyPost };

const errMsg = readError;

const AdminContent = () => {
  const [tab, setTab] = useState("committee");

  const [committee, setCommittee] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [hero, setHero] = useState({
    heroEyebrow: "", heroTitle: "", heroTagline: "",
    primaryCtaLabel: "", primaryCtaTo: "",
    secondaryCtaLabel: "", secondaryCtaTo: "",
    siteTheme: "",
    whatEyebrow: "", whatTitle: "", whatBody: "",
    whatImage: "", whatImageCaptionTitle: "", whatImageCaptionBody: "",
    packagesEyebrow: "", packagesTitle: "", packagesBody: "",
    videosEyebrow: "", videosTitle: "",
    committeeEyebrow: "", committeeTitle: "",
    blogEyebrow: "", blogTitle: "",
    partnersLabel: "",
    showDetails: true, showHighlights: true, showVideos: true,
    showCommitteePreview: true, showBlogPreview: true, showFinalCta: true,
  });
  const [heroSaving, setHeroSaving] = useState(false);
  const collections = { committee, gallery, videos, posts };
  const [editing, setEditing] = useState(null); // { type, data }
  const [pendingDelete, setPendingDelete] = useState(null);
  // Bulk-upload state (gallery tab)
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [bulkCategory, setBulkCategory] = useState("General");
  const [dragActive, setDragActive] = useState(false);

  const load = async () => {
    try {
      const [c, g, v, p, s] = await Promise.all([
        committeeApi.list(), galleryApi.list(), videosApi.list(), postsApi.list(),
        settingsApi.get(),
      ]);
      setCommittee(c);
      setGallery(g);
      setVideos(v);
      setPosts(p);
      if (s && Object.keys(s).length) setHero((h) => ({ ...h, ...s }));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveHero = async () => {
    setHeroSaving(true);
    try {
      const saved = await settingsApi.update(hero);
      setHero({ ...hero, ...saved });
      toast.success("Hero updated. Home page now reflects your changes.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setHeroSaving(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const move = async (type, id, dir) => {
    const list = [...collections[type]];
    const idx = list.findIndex((x) => x.id === id);
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    const ids = list.map((x) => x.id);
    try {
      await ENTITY_API[type].reorder(ids);
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const save = async () => {
    if (!editing) return;
    const { type, data } = editing;
    try {
      const api = ENTITY_API[type];
      const clean = { ...data };
      delete clean.id;
      if (data.id) await api.update(data.id, clean);
      else await api.create(clean);
      setEditing(null);
      await load();
      toast.success(data.id ? "Updated." : "Added.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await ENTITY_API[pendingDelete.type].remove(pendingDelete.data.id);
      setPendingDelete(null);
      await load();
      toast.success("Removed.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleBulkUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Drop image files (JPG, PNG, WebP, GIF, or AVIF).");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await galleryApi.bulkUpload(files, bulkCategory, setUploadProgress);
      await load();
      if (result.count > 0) {
        toast.success(`Uploaded ${result.count} image${result.count === 1 ? "" : "s"}.`);
      }
      if (result.errors?.length) {
        toast.error(
          `${result.errors.length} file${result.errors.length === 1 ? "" : "s"} failed: ${result.errors
            .slice(0, 2)
            .map((e) => `${e.name} (${e.error})`)
            .join(", ")}${result.errors.length > 2 ? "…" : ""}`,
        );
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const renderCard = (type, item) => {
    const thumb =
      type === "videos"
        ? `https://img.youtube.com/vi/${item.video_id}/mqdefault.jpg`
        : item.img || item.src;
    const title =
      type === "committee" ? item.name : type === "videos" ? item.title : item.title;
    const subtitle =
      type === "committee"
        ? item.role
        : type === "videos"
          ? `${item.tag || "YouTube"} · ${item.duration || ""}`
          : type === "posts"
            ? `${item.tag || ""}${item.date ? " · " + item.date : ""}`
            : item.category;
    const detail = item.bio || item.caption || item.excerpt || "";
    return (
      <motion.div
        key={item.id}
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4"
        data-testid={`admin-${type}-row-${item.id}`}
      >
        <img
          src={thumb}
          alt=""
          className="h-14 w-14 shrink-0 rounded-xl bg-apicon-mist object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-lg font-semibold text-apicon-navy">
            {title}
          </div>
          <div className="truncate text-xs uppercase tracking-[0.16em] text-apicon-green">
            {subtitle}
          </div>
          <div className="mt-1 line-clamp-1 text-sm text-gray-500">
            {detail}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => move(type, item.id, "up")}
            className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-apicon-navy"
            aria-label="Move up"
            data-testid={`admin-${type}-up-${item.id}`}
          >
            <MoveUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => move(type, item.id, "down")}
            className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-apicon-navy"
            aria-label="Move down"
            data-testid={`admin-${type}-down-${item.id}`}
          >
            <MoveDown className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing({ type, data: { ...item } })}
            className="rounded-full p-1.5 text-apicon-navy hover:bg-apicon-mist"
            aria-label="Edit"
            data-testid={`admin-${type}-edit-${item.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPendingDelete({ type, data: item })}
            className="rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
            aria-label="Delete"
            data-testid={`admin-${type}-delete-${item.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  const isCommittee = editing?.type === "committee";
  const isVideo = editing?.type === "videos";
  const isPost = editing?.type === "posts";

  return (
    <div className="relative bg-white" data-testid="page-admin-content">
      {/* Header */}
      <section className="relative overflow-hidden bg-apicon-navy py-14 md:py-16">
        <div className="pointer-events-none absolute inset-0 opacity-[0.08] heritage-pattern" />
        <div className="relative mx-auto flex w-full max-w-7xl flex-wrap items-end justify-between gap-6 px-5 md:px-8">
          <div>
            <Link
              to="/admin"
              className="mb-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.16em] text-apicon-gold/90 hover:text-apicon-gold"
              data-testid="admin-content-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to registrations
            </Link>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-gold">
              Admin · Content
            </div>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Committee &amp; Gallery
            </h1>
            <p className="mt-2 max-w-xl text-sm text-white/70">
              Add, edit, reorder or remove committee members and gallery
              images. Changes go live instantly for delegates.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-5 py-10 md:px-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList
            className="mb-8 rounded-full bg-apicon-mist p-1"
            data-testid="admin-content-tabs"
          >
            <TabsTrigger
              value="hero"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-hero"
            >
              <HomeIcon className="mr-2 h-4 w-4" /> Hero
            </TabsTrigger>
            <TabsTrigger
              value="committee"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-committee"
            >
              <Users className="mr-2 h-4 w-4" /> Committee
              <span className="ml-2 text-[10px] font-semibold opacity-70">
                {committee.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="gallery"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-gallery"
            >
              <ImageIcon className="mr-2 h-4 w-4" /> Gallery
              <span className="ml-2 text-[10px] font-semibold opacity-70">
                {gallery.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="videos"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-videos"
            >
              <Youtube className="mr-2 h-4 w-4" /> Videos
              <span className="ml-2 text-[10px] font-semibold opacity-70">
                {videos.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="posts"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-posts"
            >
              <Newspaper className="mr-2 h-4 w-4" /> Blog
              <span className="ml-2 text-[10px] font-semibold opacity-70">
                {posts.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="images"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-images"
            >
              <ImageIcon className="mr-2 h-4 w-4" /> Site images
            </TabsTrigger>
            <TabsTrigger
              value="extras"
              className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
              data-testid="admin-content-tab-extras"
            >
              <Sparkles className="mr-2 h-4 w-4" /> Extras
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hero" className="mt-0" data-testid="admin-hero-panel">
            <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
              <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                  Home hero
                </div>
                <div className="mt-1 font-display text-2xl font-semibold text-apicon-navy">
                  The first thing visitors see
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Edits go live instantly on the public site.
                </p>
              </div>

              {/* ============ Section visibility toggles ============ */}
              <div
                className="mb-8 rounded-2xl border border-apicon-navy/10 bg-apicon-mist/40 p-5"
                data-testid="hero-editor-sections-toggle"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-navy">
                  Home page sections
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Turn individual sections on or off — Hero, Registration
                  Packages and Footer stay visible by design.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    { key: "showDetails", label: "Conference details (bento)" },
                    { key: "showHighlights", label: "Highlights strip (300+ delegates, sessions…)" },
                    { key: "showVideos", label: "Video highlights carousel" },
                    { key: "showCommitteePreview", label: "Committee preview grid" },
                    { key: "showBlogPreview", label: "From-the-journal (blog) preview" },
                    { key: "showFinalCta", label: "Final call-to-action banner" },
                  ].map((s) => (
                    <label
                      key={s.key}
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-apicon-navy/40"
                      data-testid={`hero-editor-toggle-${s.key}`}
                    >
                      <span className="text-sm font-medium text-apicon-navy">
                        {s.label}
                      </span>
                      <input
                        type="checkbox"
                        role="switch"
                        checked={!!hero[s.key]}
                        onChange={(e) =>
                          setHero({ ...hero, [s.key]: e.target.checked })
                        }
                        className="h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-gray-300 transition-colors before:block before:h-4 before:w-4 before:translate-x-0.5 before:translate-y-0.5 before:rounded-full before:bg-white before:transition-transform checked:bg-apicon-navy checked:before:translate-x-[18px]"
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid gap-5">
                <Field
                  label="Eyebrow (small line above the title)"
                  value={hero.heroEyebrow}
                  testId="hero-editor-eyebrow"
                  onChange={(v) => setHero({ ...hero, heroEyebrow: v })}
                />
                <Area
                  label="Title"
                  value={hero.heroTitle}
                  testId="hero-editor-title"
                  onChange={(v) => setHero({ ...hero, heroTitle: v })}
                />
                <Area
                  label="Tagline / description"
                  value={hero.heroTagline}
                  testId="hero-editor-tagline"
                  onChange={(v) => setHero({ ...hero, heroTagline: v })}
                />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Primary CTA label"
                    value={hero.primaryCtaLabel}
                    testId="hero-editor-primary-label"
                    onChange={(v) => setHero({ ...hero, primaryCtaLabel: v })}
                  />
                  <Field
                    label="Primary CTA link"
                    value={hero.primaryCtaTo}
                    testId="hero-editor-primary-to"
                    placeholder="/register"
                    onChange={(v) => setHero({ ...hero, primaryCtaTo: v })}
                  />
                  <Field
                    label="Secondary CTA label"
                    value={hero.secondaryCtaLabel}
                    testId="hero-editor-secondary-label"
                    onChange={(v) => setHero({ ...hero, secondaryCtaLabel: v })}
                  />
                  <Field
                    label="Secondary CTA link"
                    value={hero.secondaryCtaTo}
                    testId="hero-editor-secondary-to"
                    placeholder="/committee"
                    onChange={(v) => setHero({ ...hero, secondaryCtaTo: v })}
                  />
                </div>

                {/* Site-wide theme / tagline */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    Conference theme (site-wide)
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    A short line that appears on the hero as the conference&rsquo;s
                    guiding theme.
                  </p>
                  <div className="mt-4">
                    <Area
                      label="Theme / motto"
                      value={hero.siteTheme}
                      testId="hero-editor-site-theme"
                      onChange={(v) => setHero({ ...hero, siteTheme: v })}
                      placeholder="e.g. Advancing Internal Medicine: Bridging Science and Compassion"
                    />
                  </div>
                </div>

                {/* What-to-expect editable block */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    &ldquo;What to expect&rdquo; section
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    The pitch block on the homepage above the video highlights.
                  </p>
                  <div className="mt-4 grid gap-5">
                    <Field
                      label="Eyebrow"
                      value={hero.whatEyebrow}
                      testId="hero-editor-what-eyebrow"
                      placeholder="What to expect"
                      onChange={(v) => setHero({ ...hero, whatEyebrow: v })}
                    />
                    <Area
                      label="Title (allows a line break with the word 'the')"
                      value={hero.whatTitle}
                      testId="hero-editor-what-title"
                      placeholder="A conference shaped around the delegate's rhythm."
                      onChange={(v) => setHero({ ...hero, whatTitle: v })}
                    />
                    <Area
                      label="Body copy"
                      value={hero.whatBody}
                      testId="hero-editor-what-body"
                      onChange={(v) => setHero({ ...hero, whatBody: v })}
                    />
                    <Field
                      label="Image URL"
                      value={hero.whatImage}
                      testId="hero-editor-what-image"
                      placeholder="https://…"
                      onChange={(v) => setHero({ ...hero, whatImage: v })}
                    />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Image caption — top line"
                        value={hero.whatImageCaptionTitle}
                        testId="hero-editor-what-cap-title"
                        placeholder="Auditorium · TMCH"
                        onChange={(v) => setHero({ ...hero, whatImageCaptionTitle: v })}
                      />
                      <Field
                        label="Image caption — main line"
                        value={hero.whatImageCaptionBody}
                        testId="hero-editor-what-cap-body"
                        placeholder="Purpose-built halls…"
                        onChange={(v) => setHero({ ...hero, whatImageCaptionBody: v })}
                      />
                    </div>
                  </div>
                </div>

                {/* Registration Packages section header */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    &ldquo;Registration Packages&rdquo; section header
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Copy shown above the 4 pricing cards on the homepage.
                  </p>
                  <div className="mt-4 grid gap-5">
                    <Field
                      label="Eyebrow"
                      value={hero.packagesEyebrow}
                      testId="hero-editor-packages-eyebrow"
                      placeholder="Registration Packages"
                      onChange={(v) => setHero({ ...hero, packagesEyebrow: v })}
                    />
                    <Area
                      label="Title"
                      value={hero.packagesTitle}
                      testId="hero-editor-packages-title"
                      placeholder="Simple, transparent pricing."
                      onChange={(v) => setHero({ ...hero, packagesTitle: v })}
                    />
                    <Area
                      label="Body copy"
                      value={hero.packagesBody}
                      testId="hero-editor-packages-body"
                      onChange={(v) => setHero({ ...hero, packagesBody: v })}
                    />
                  </div>
                </div>

                {/* Video highlights header */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    &ldquo;Video Highlights&rdquo; section header
                  </div>
                  <div className="mt-4 grid gap-5 sm:grid-cols-2">
                    <Field
                      label="Eyebrow pill"
                      value={hero.videosEyebrow}
                      testId="hero-editor-videos-eyebrow"
                      placeholder="Video highlights"
                      onChange={(v) => setHero({ ...hero, videosEyebrow: v })}
                    />
                    <Field
                      label="Subtitle line"
                      value={hero.videosTitle}
                      testId="hero-editor-videos-title"
                      placeholder="A quick look at last December."
                      onChange={(v) => setHero({ ...hero, videosTitle: v })}
                    />
                  </div>
                </div>

                {/* Committee preview header */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    &ldquo;Organising Committee&rdquo; section header
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Copy shown above the committee preview grid on the homepage.
                  </p>
                  <div className="mt-4 grid gap-5">
                    <Field
                      label="Eyebrow"
                      value={hero.committeeEyebrow}
                      testId="hero-editor-committee-eyebrow"
                      placeholder="Organising committee"
                      onChange={(v) => setHero({ ...hero, committeeEyebrow: v })}
                    />
                    <Area
                      label="Title"
                      value={hero.committeeTitle}
                      testId="hero-editor-committee-title"
                      placeholder="Led by physicians you already respect."
                      onChange={(v) => setHero({ ...hero, committeeTitle: v })}
                    />
                  </div>
                </div>

                {/* Blog preview header */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    &ldquo;From the journal&rdquo; (Blog) section header
                  </div>
                  <div className="mt-4 grid gap-5">
                    <Field
                      label="Eyebrow"
                      value={hero.blogEyebrow}
                      testId="hero-editor-blog-eyebrow"
                      placeholder="From the journal"
                      onChange={(v) => setHero({ ...hero, blogEyebrow: v })}
                    />
                    <Area
                      label="Title"
                      value={hero.blogTitle}
                      testId="hero-editor-blog-title"
                      placeholder="Notes ahead of December."
                      onChange={(v) => setHero({ ...hero, blogTitle: v })}
                    />
                  </div>
                </div>

                {/* Footer partners label */}
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    Footer — umbrella / partners label
                  </div>
                  <div className="mt-4">
                    <Field
                      label="Label above the two logo circles"
                      value={hero.partnersLabel}
                      testId="hero-editor-partners-label"
                      placeholder="Under the auspices of"
                      onChange={(v) => setHero({ ...hero, partnersLabel: v })}
                    />
                  </div>
                </div>

                <div className="mt-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                    {hero.heroEyebrow || "Eyebrow"}
                  </div>
                  <div className="mt-2 font-display text-3xl font-bold leading-tight text-apicon-navy">
                    {hero.heroTitle || "Your hero title"}
                  </div>
                  <div className="mt-3 max-w-lg text-sm text-gray-600">
                    {hero.heroTagline || "Your tagline appears here."}
                  </div>
                  {hero.siteTheme && (
                    <div className="mt-4 inline-block rounded-full bg-apicon-gold/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-apicon-navy">
                      {hero.siteTheme}
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={saveHero}
                    disabled={heroSaving}
                    className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400] disabled:opacity-70"
                    data-testid="hero-editor-save"
                  >
                    {heroSaving ? "Saving…" : "Save home page"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="committee" className="mt-0">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {committee.length} member{committee.length !== 1 ? "s" : ""}
              </div>
              <Button
                onClick={() => setEditing({ type: "committee", data: { ...emptyCommittee } })}
                className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]"
                data-testid="admin-committee-new"
              >
                <Plus className="mr-1 h-4 w-4" /> Add member
              </Button>
            </div>
            <div className="space-y-3">
              {committee.map((c) => renderCard("committee", c))}
              {committee.length === 0 && (
                <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
                  No committee members yet — add your first one.
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="mt-0">
            {/* Bulk upload dropzone */}
            <label
              htmlFor="bulk-upload-input"
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (uploading) return;
                handleBulkUpload(e.dataTransfer.files);
              }}
              className={cn(
                "group relative mb-5 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-8 text-center transition-all duration-200",
                dragActive
                  ? "border-apicon-navy bg-apicon-mist/60"
                  : "border-gray-200 bg-white hover:border-apicon-navy/60 hover:bg-apicon-mist/40",
                uploading && "pointer-events-none opacity-90",
              )}
              data-testid="gallery-bulk-dropzone"
            >
              <input
                id="bulk-upload-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                multiple
                className="sr-only"
                disabled={uploading}
                onChange={(e) => handleBulkUpload(e.target.files)}
                data-testid="gallery-bulk-input"
              />
              <span
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-full transition-colors",
                  dragActive ? "bg-apicon-navy text-white" : "bg-apicon-mist text-apicon-navy",
                )}
              >
                <UploadCloud className="h-6 w-6" />
              </span>
              <div>
                <div className="font-display text-lg font-semibold text-apicon-navy">
                  {uploading
                    ? `Uploading… ${uploadProgress}%`
                    : "Drop images here, or click to browse"}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  JPG · PNG · WebP · GIF · AVIF — up to 8 MB each
                </div>
              </div>
              {/* Progress bar */}
              {uploading && (
                <div className="mt-1 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-apicon-mist">
                  <div
                    className="h-full rounded-full bg-apicon-navy transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="gallery-bulk-progress"
                  />
                </div>
              )}
              {/* Category input — pill overlay to top-right, click doesn't propagate */}
              <div
                className="mt-1 flex items-center gap-2 text-xs text-gray-500"
                onClick={(e) => e.preventDefault()}
              >
                <span>Category:</span>
                <Input
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="h-8 w-40 rounded-full border-gray-200 text-center text-xs focus-visible:ring-apicon-navy"
                  placeholder="e.g. Cultural"
                  disabled={uploading}
                  data-testid="gallery-bulk-category"
                />
              </div>
            </label>

            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {gallery.length} image{gallery.length !== 1 ? "s" : ""}
              </div>
              <Button
                onClick={() => setEditing({ type: "gallery", data: { ...emptyGallery } })}
                className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]"
                data-testid="admin-gallery-new"
              >
                <Plus className="mr-1 h-4 w-4" /> Add image (by URL)
              </Button>
            </div>
            <div className="space-y-3">
              {gallery.map((g) => renderCard("gallery", g))}
            </div>
          </TabsContent>

          <TabsContent value="videos" className="mt-0">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {videos.length} video{videos.length !== 1 ? "s" : ""} · YouTube IDs
              </div>
              <Button
                onClick={() => setEditing({ type: "videos", data: { ...emptyVideo } })}
                className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]"
                data-testid="admin-videos-new"
              >
                <Plus className="mr-1 h-4 w-4" /> Add video
              </Button>
            </div>
            <div className="space-y-3">
              {videos.map((v) => renderCard("videos", v))}
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-0">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {posts.length} post{posts.length !== 1 ? "s" : ""}
              </div>
              <Button
                onClick={() => setEditing({ type: "posts", data: { ...emptyPost } })}
                className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]"
                data-testid="admin-posts-new"
              >
                <Plus className="mr-1 h-4 w-4" /> Add post
              </Button>
            </div>
            <div className="space-y-3">
              {posts.map((p) => renderCard("posts", p))}
            </div>
          </TabsContent>

          <TabsContent value="images" className="mt-0">
            <SiteImagesPanel />
          </TabsContent>

          <TabsContent value="extras" className="mt-0" data-testid="admin-extras-panel">
            <AdminExtras />
          </TabsContent>
        </Tabs>
      </section>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="admin-content-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-apicon-navy">
              {editing?.data?.id ? "Edit " : "New "}
              {isCommittee ? "committee member" : isVideo ? "video" : isPost ? "blog post" : "gallery image"}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Changes are saved to the live site immediately.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4 py-2">
              {isCommittee && (
                <>
                  <Field label="Name" value={editing.data.name} testId="editor-input-name"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, name: v } })} />
                  <Field label="Role" value={editing.data.role} testId="editor-input-role"
                    placeholder="e.g. Organising Chairperson"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, role: v } })} />
                  <div className="grid gap-2">
                    <Label className="text-sm text-apicon-navy">Section on Committee page</Label>
                    <select
                      value={editing.data.group || "leadership"}
                      onChange={(e) =>
                        setEditing({ ...editing, data: { ...editing.data, group: e.target.value } })
                      }
                      className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-apicon-navy focus:border-apicon-navy focus:outline-none focus:ring-2 focus:ring-apicon-navy/20"
                      data-testid="editor-input-group"
                    >
                      <option value="patron">Patrons — Honorary leadership</option>
                      <option value="advisors">Advisors</option>
                      <option value="leadership">Core organising team</option>
                      <option value="organising_vice_chairs">Organising Vice-Chairpersons</option>
                      <option value="organising_secretary">Organising Secretary</option>
                      <option value="joint_secretaries">Joint Secretaries</option>
                      <option value="treasurer">Treasurer</option>
                      <option value="scientific">Scientific programme</option>
                      <option value="scientific_secretary">Scientific Secretary</option>
                      <option value="scientific_asst_secretary">Scientific Asst Secretary</option>
                      <option value="medicine_update_editor">Medicine Update Editor</option>
                      <option value="pg_program_committee">Post Graduate Program Committee</option>
                      <option value="functional">Functional chairs</option>
                      <option value="other">Also on the committee</option>
                    </select>
                    <p className="text-xs text-gray-500">
                      Controls which tier this member appears under on the public /committee page.
                    </p>
                  </div>
                  {/* Photo — upload from device (preferred) or paste a URL */}
                  <div className="grid gap-2">
                    <Label className="text-sm text-apicon-navy">Photo</Label>
                    <div className="flex flex-wrap items-center gap-3">
                      {editing.data.img ? (
                        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-apicon-mist ring-1 ring-gray-200">
                          <img
                            src={
                              editing.data.img.startsWith("/api/")
                                ? `${process.env.REACT_APP_BACKEND_URL || ""}${editing.data.img}`
                                : editing.data.img
                            }
                            alt="preview"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-apicon-mist text-apicon-navy/40 ring-1 ring-gray-200">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      )}
                      <div className="flex flex-1 flex-wrap items-center gap-2">
                        <label
                          className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-apicon-navy px-4 py-2 text-xs font-semibold text-white hover:bg-[#082C5A]"
                          data-testid="editor-committee-upload"
                        >
                          <UploadCloud className="h-3.5 w-3.5" />
                          {editing.data.img ? "Replace photo" : "Upload photo"}
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              try {
                                const res = await siteImagesApi.uploadPhoto(f);
                                setEditing({
                                  ...editing,
                                  data: { ...editing.data, img: res.url },
                                });
                                toast.success("Photo uploaded.");
                              } catch (err) {
                                toast.error(readError(err) || "Upload failed.");
                              } finally {
                                e.target.value = "";
                              }
                            }}
                          />
                        </label>
                        {editing.data.img && (
                          <button
                            type="button"
                            onClick={() =>
                              setEditing({ ...editing, data: { ...editing.data, img: "" } })
                            }
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-medium text-apicon-navy hover:border-apicon-navy hover:bg-apicon-mist"
                          >
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <Input
                      value={editing.data.img || ""}
                      placeholder="…or paste an image URL"
                      className="mt-1 h-10 rounded-xl border-gray-200 text-xs focus-visible:ring-apicon-navy"
                      onChange={(e) =>
                        setEditing({ ...editing, data: { ...editing.data, img: e.target.value } })
                      }
                      data-testid="editor-input-img"
                    />
                  </div>
                  <Area label="Short bio" value={editing.data.bio} testId="editor-input-bio"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, bio: v } })} />
                </>
              )}
              {editing.type === "gallery" && (
                <>
                  <Field label="Title" value={editing.data.title} testId="editor-input-title"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, title: v } })} />
                  <Field label="Category" value={editing.data.category} testId="editor-input-category"
                    placeholder="e.g. Cultural, Sessions, Tinsukia"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, category: v } })} />
                  <Field label="Image URL" value={editing.data.src} testId="editor-input-src"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, src: v } })} />
                  <Area label="Caption" value={editing.data.caption} testId="editor-input-caption"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, caption: v } })} />
                </>
              )}
              {isVideo && (
                <>
                  <div className="grid gap-2">
                    <Label className="text-sm text-apicon-navy">
                      YouTube link or Video ID
                    </Label>
                    <Input
                      value={editing.data.video_id || ""}
                      placeholder="Paste any YouTube link — we'll extract the ID"
                      className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
                      data-testid="editor-input-video-id"
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          data: { ...editing.data, video_id: extractYoutubeId(e.target.value) },
                        })
                      }
                    />
                    <p className="text-xs text-gray-500">
                      Works with{" "}
                      <span className="font-mono text-gray-700">youtube.com/watch?v=…</span>,{" "}
                      <span className="font-mono text-gray-700">youtu.be/…</span>,{" "}
                      <span className="font-mono text-gray-700">/shorts/…</span>, or a plain
                      11-character ID.
                    </p>
                  </div>
                  <Field label="Title" value={editing.data.title} testId="editor-input-title"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, title: v } })} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Tag" value={editing.data.tag} testId="editor-input-tag"
                      placeholder="e.g. Plenary, Cultural"
                      onChange={(v) => setEditing({ ...editing, data: { ...editing.data, tag: v } })} />
                    <Field label="Duration" value={editing.data.duration} testId="editor-input-duration"
                      placeholder="e.g. 4:12"
                      onChange={(v) => setEditing({ ...editing, data: { ...editing.data, duration: v } })} />
                  </div>
                  {/^[A-Za-z0-9_-]{11}$/.test(editing.data.video_id || "") && (
                    <div className="overflow-hidden rounded-xl bg-black">
                      <div className="aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${editing.data.video_id}?rel=0`}
                          className="h-full w-full border-0"
                          title="preview"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {isPost && (
                <>
                  <Field label="Title" value={editing.data.title} testId="editor-input-title"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, title: v } })} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Tag" value={editing.data.tag} testId="editor-input-tag"
                      placeholder="e.g. Programme, Registration"
                      onChange={(v) => setEditing({ ...editing, data: { ...editing.data, tag: v } })} />
                    <Field label="Date label" value={editing.data.date} testId="editor-input-date"
                      placeholder="e.g. Feb 12, 2026"
                      onChange={(v) => setEditing({ ...editing, data: { ...editing.data, date: v } })} />
                  </div>
                  <Field label="Cover image URL" value={editing.data.img} testId="editor-input-img"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, img: v } })} />
                  <Area label="Excerpt" value={editing.data.excerpt} testId="editor-input-excerpt"
                    onChange={(v) => setEditing({ ...editing, data: { ...editing.data, excerpt: v } })} />
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditing(null)}
              className="rounded-full text-gray-600 hover:bg-gray-100"
              data-testid="admin-content-editor-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]"
              data-testid="admin-content-editor-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!pendingDelete}
        onOpenChange={(v) => !v && setPendingDelete(null)}
      >
        <DialogContent className="max-w-md rounded-3xl" data-testid="admin-content-delete">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-apicon-navy">
              Remove this entry?
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              This action can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              className="rounded-full text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
              data-testid="admin-content-delete-confirm"
            >
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- small controlled inputs ----------
const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div className="grid gap-2">
    <Label className="text-sm text-apicon-navy">{label}</Label>
    <Input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
      data-testid={testId}
    />
  </div>
);

const Area = ({ label, value, onChange, testId }) => (
  <div className="grid gap-2">
    <Label className="text-sm text-apicon-navy">{label}</Label>
    <Textarea
      rows={4}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-2xl border-gray-200 text-sm focus-visible:ring-apicon-navy"
      data-testid={testId}
    />
  </div>
);

export default AdminContent;
